// --- UPDATE (FlowSense AI Explainer + real-data pass)
//   • Crash fix: a non-array response (e.g. { success, data: { … } }) broke .filter; the list is
//     read with asList(). Missing fix rate / resolution time show "—" and an "Unrated" tier
//     (NaN used to fall into "Needs Training").
//   • Live: refreshes when work orders are completed / reassigned (work_order_events), debounced.
//   • Fullscreen now works; the selected row is readable in dark mode.
//   • "Explain technician performance" FlowSense AI explainer: who needs coaching, workload
//     imbalance, slow closers — from the real rows — plus a plain-language AI explanation.
// --- END UPDATE
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  Heading,
  Flex,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  InputGroup,
  InputLeftElement,
  Input,
  Select,
  Button,
  Icon,
  HStack,
  VStack,
  Badge,
  Tooltip,
  useColorModeValue,
  Spinner,
  Progress,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  SimpleGrid,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Switch,
  Collapse,
  Code,
  Divider,
  useToast,
  Avatar,
  AvatarBadge,
  Tag,
  TagLabel,
  TagLeftIcon,
} from '@chakra-ui/react';
import {
  MdSearch,
  MdFilterList,
  MdWarning,
  MdRefresh,
  MdDownload,
  MdSettings,
  MdBugReport,
  MdFullscreen,
  MdFullscreenExit,
  MdArrowUpward,
  MdArrowDownward,
  MdUnfoldMore,
  MdStar,
  MdTrendingUp,
  MdTrendingDown,
  MdPerson,
  MdChevronLeft,
  MdChevronRight,
} from 'react-icons/md';
import { FiAward, FiAlertTriangle, FiCheckCircle } from 'react-icons/fi';
import Card from '../card/Cards';
import analyticsService, { asList, liveEventBody } from '../../services/analyticsService';
import webSocketService from '../../services/websocketService'; // --- UPDATE
import FlowSenseAIExplainer from '../ai/FlowSenseAIExplainer'; // --- UPDATE

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────
const PERFORMANCE_TIERS = {
  EXCELLENT:    { label: 'Excellent',      scheme: 'green',  icon: FiAward,        fixMin: 0.8,  resMax: 24  },
  GOOD:         { label: 'Good',           scheme: 'blue',   icon: FiCheckCircle,  fixMin: 0.6,  resMax: 48  },
  NEEDS_WORK:   { label: 'Needs Training', scheme: 'red',    icon: FiAlertTriangle, fixMin: 0,   resMax: Infinity },
};

// --- UPDATE: no data is not "needs training"
const UNRATED_TIER = { label: 'Unrated', scheme: 'gray', icon: FiAlertTriangle };
const fmtNum = (v, d = 1, suffix = '') => (Number.isFinite(v) ? `${v.toFixed(d)}${suffix}` : '—');
// --- END UPDATE

const getTier = (fixRate, resTime) => {
  if (!Number.isFinite(fixRate) || !Number.isFinite(resTime)) return UNRATED_TIER; // --- UPDATE
  if (fixRate > 0.8 && resTime < 24)               return PERFORMANCE_TIERS.EXCELLENT;
  if (fixRate >= 0.6 && resTime <= 48)              return PERFORMANCE_TIERS.GOOD;
  return PERFORMANCE_TIERS.NEEDS_WORK;
};

const getFtfColor = (rate) => {
  if (rate >= 0.8)  return 'green';
  if (rate >= 0.6)  return 'blue';
  if (rate >= 0.4)  return 'yellow';
  return 'red';
};

const getResTimeColor = (hours) => {
  if (hours <= 8)   return 'green.500';
  if (hours <= 24)  return 'blue.500';
  if (hours <= 48)  return 'orange.400';
  return 'red.500';
};

const PAGE_SIZE_OPTIONS = [5, 10, 15, 25, 50];

// ─────────────────────────────────────────────────────────────────────────────
// Sortable Column Header
// ─────────────────────────────────────────────────────────────────────────────
const SortableTh = React.memo(({ label, field, sortConfig, onSort, isNumeric, tooltip }) => {
  const isActive    = sortConfig.field === field;
  const activeColor = useColorModeValue('blue.600', 'blue.300');
  const mutedColor  = useColorModeValue('gray.400', 'gray.500');

  return (
    <Th
      isNumeric={isNumeric}
      cursor="pointer"
      userSelect="none"
      onClick={() => onSort(field)}
      _hover={{ color: activeColor }}
      color={isActive ? activeColor : undefined}
      whiteSpace="nowrap"
    >
      <Tooltip label={tooltip || `Sort by ${label}`} placement="top" hasArrow>
        <HStack spacing={1} justify={isNumeric ? 'flex-end' : 'flex-start'} display="inline-flex">
          <Text>{label}</Text>
          {isActive ? (
            sortConfig.direction === 'asc'
              ? <Icon as={MdArrowUpward}   boxSize={3} color={activeColor} />
              : <Icon as={MdArrowDownward} boxSize={3} color={activeColor} />
          ) : (
            <Icon as={MdUnfoldMore} boxSize={3} color={mutedColor} />
          )}
        </HStack>
      </Tooltip>
    </Th>
  );
});

// ─────────────────────────────────────────────────────────────────────────────
// Technician Row — enhanced but original field keys preserved
// ─────────────────────────────────────────────────────────────────────────────
const TechnicianRow = React.memo(({
  tech, rank, textColor, tableRowHoverBg, visibleColumns, isSelected, onSelect
}) => {
  const fixRate = parseFloat(tech.first_time_fix_rate);
  const resTime = parseFloat(tech.avg_resolution_time_hours);
  const tier = getTier(fixRate, resTime);
  const ftfColor = getFtfColor(fixRate);

  const selectedBg = useColorModeValue('blue.50', 'whiteAlpha.100'); // --- UPDATE: readable in dark mode
  const rankColors = ['yellow.400', 'gray.400', 'orange.400'];
  const rankColor = rank <= 3 ? rankColors[rank - 1] : undefined;

  return (
    <Tr
      _hover={{ bg: tableRowHoverBg, transform: 'scale(1.001)' }}
      transition="all 0.15s"
      cursor="pointer"
      onClick={onSelect}
      bg={isSelected ? selectedBg : undefined}
      borderLeft="3px solid"
      borderLeftColor={tier.scheme + '.400'}
    >
      {visibleColumns.rank && (
        <Td w="40px" textAlign="center">
          {rank <= 3 ? <Icon as={MdStar} color={rankColor} boxSize={4} /> : <Text fontSize="xs" color="gray.400">#{rank}</Text>}
        </Td>
      )}
      {visibleColumns.name && (
        <Td>
          <HStack spacing={2}>
            <Avatar size="xs" name={tech.technician_name || undefined}><AvatarBadge boxSize="1.1em" bg={tier.scheme + '.400'} /></Avatar>
            <Text fontWeight="semibold" color={textColor} fontSize="sm">{tech.technician_name || 'Unnamed technician'}</Text>
          </HStack>
        </Td>
      )}
      {visibleColumns.completedWOs && <Td isNumeric><Badge colorScheme="blue" variant="solid" borderRadius="full" px={2}>{tech.completed_work_orders}</Badge></Td>}
      {visibleColumns.resolutionTime && <Td isNumeric><Text fontSize="sm" fontWeight="bold" color={Number.isFinite(resTime) ? getResTimeColor(resTime) : 'gray.400'}>{fmtNum(resTime, 1, 'h')}</Text></Td>}
      {visibleColumns.ftfRate && (
        <Td>
          <VStack spacing={1} align="stretch" minW="90px">
            <Text fontSize="xs" fontWeight="bold">{fmtNum(fixRate * 100, 1, '%')}</Text>
            <Progress value={Number.isFinite(fixRate) ? fixRate * 100 : 0} colorScheme={Number.isFinite(fixRate) ? ftfColor : 'gray'} size="xs" borderRadius="full" />
          </VStack>
        </Td>
      )}
      {visibleColumns.performance && (
        <Td>
          <Badge colorScheme={tier.scheme} variant="subtle" borderRadius="full" px={2} display="flex" alignItems="center" gap={1} w="fit-content">
            <Icon as={tier.icon} boxSize={3} /> {tier.label}
          </Badge>
        </Td>
      )}
    </Tr>
  );
});

// ─────────────────────────────────────────────────────────────────────────────
// Main Widget
// ─────────────────────────────────────────────────────────────────────────────
const TechnicianPerformanceWidget = ({
  data: externalData,
  isLoading: externalLoading,
  enableDebug  = false,
  enableExport = true,
  historicalData = null,
}) => {
  // ── Theme ──────────────────────────────────────────────────────────────────
  const cardBg          = useColorModeValue('white', 'gray.800');
  const borderColor     = useColorModeValue('gray.200', 'gray.600');
  const tableHeadBg     = useColorModeValue('gray.50', 'gray.700');
  const tableRowHoverBg = useColorModeValue('gray.50', 'gray.700');
  const textColor       = useColorModeValue('gray.700', 'gray.200');
  const mutedColor      = useColorModeValue('gray.500', 'gray.400');
  const inputBg         = useColorModeValue('gray.50', 'gray.700');
  const debugBg         = useColorModeValue('gray.50', 'gray.700');

  // ── State ──────────────────────────────────────────────────────────────────
  const [searchTerm,      setSearchTerm]      = useState('');
  const [filterMetric,    setFilterMetric]    = useState('all');
  const [sortConfig,      setSortConfig]      = useState({ field: 'completed_work_orders', direction: 'desc' });
  const [currentPage,     setCurrentPage]     = useState(1);
  const [pageSize,        setPageSize]        = useState(10);
  const [selectedRow,     setSelectedRow]     = useState(null);
  const [isFullscreen,    setIsFullscreen]    = useState(false);
  const [showDebug,       setShowDebug]       = useState(enableDebug);
  const [debugLogs,       setDebugLogs]       = useState([]);
  const [performanceMetrics, setPerformanceMetrics] = useState({ renderTime: 0, dataPoints: 0, lastUpdate: null });

  const [visibleColumns, setVisibleColumns] = useState({
    rank:           true,
    name:           true,
    completedWOs:   true,
    resolutionTime: true,
    ftfRate:        true,
    performance:    true,
  });

  // ── 🚀 ADVANCED DEBUGGING: Render Tracking ────────────────────────────────
  const renderCount = useRef(0);
  renderCount.current += 1;

  // ── Internal data fetching ─────────────────────────────────────────────────
  const [internalData,    setInternalData]    = useState([]);
  const [internalLoading, setInternalLoading] = useState(false);

  const activeData    = externalData || internalData;
  const activeLoading = externalLoading !== undefined ? externalLoading : internalLoading;

  const { isOpen: isSettingsOpen, onOpen: onSettingsOpen, onClose: onSettingsClose } = useDisclosure();
  const toast = useToast();

  // ── 🚀 ADVANCED DEBUGGING: Enhanced Debug Logger ──────────────────────────
  const addDebugLog = useCallback((message, type = 'info', details = null) => {
    if (!enableDebug && !showDebug) return;
    const timestamp = new Date().toISOString();
    let safeDetails = details;
    try {
      if (details instanceof Error) {
        safeDetails = { message: details.message, name: details.name, stack: details.stack };
      } else if (typeof details === 'object' && details !== null) {
        safeDetails = JSON.parse(JSON.stringify(details, (k, v) =>
          typeof v === 'function' ? '[Function]' : v
        ));
      }
    } catch (e) {
      safeDetails = '[Unserializable Object]';
    }
    const log = { timestamp, type, message, details: safeDetails };
    setDebugLogs(prev => [log, ...prev].slice(0, 100));
    if (type === 'error')     { console.error(`[TechPerformance] ❌ ${message}`, details); console.trace(); }
    else if (type === 'warn') { console.warn(`[TechPerformance] ⚠️ ${message}`, details); }
    else                      { console.log(`[TechPerformance] ℹ️ ${message}`, details); }
  }, [enableDebug, showDebug]);

  // ── Lifecycle Monitors ─────────────────────────────────────────────────────
  useEffect(() => {
    if (renderCount.current % 5 === 0) {
      addDebugLog(`Render Cycle (Count: ${renderCount.current})`, 'info', { filterMetric, sortConfig, currentPage });
    }
  });

  useEffect(() => {
    addDebugLog('Data Source Changed', 'info', {
      hasExternalData: !!externalData,
      activeDataSize:  activeData?.length ?? 0,
      sampleHead:      activeData?.slice(0, 2),
    });
  }, [activeData, externalData, addDebugLog]);

  // ── 🚀 CORE LOGIC: Fetch — DO NOT MODIFY ──────────────────────────────────
  const fetchTechnicianData = useCallback(async (forceRefresh = false) => {
    if (externalData) {
      addDebugLog('fetchTechnicianData skipped: externalData prop managing state', 'warn');
      return;
    }
    setInternalLoading(true);
    const startTime = performance.now();
    addDebugLog(`Initiating data fetch (forceRefresh: ${forceRefresh})`, 'info');
    try {
      // --- UPDATE: the dedicated route (/analytics/technician-performance); the widget name returned 404
      //   on servers without the new widget alias, so fall back to it only if the route is missing
      let response;
      try { response = await analyticsService.fetchTechnicianPerformance(); }
      catch (e) { if (e?.status !== 404) throw e; response = await analyticsService.getWidgetData('technician-performance'); }
      // --- END UPDATE
      const parsedData = asList(response); // --- UPDATE: always an array
      setInternalData(parsedData);
      const fetchTime = performance.now() - startTime;
      addDebugLog('Technician data fetched successfully', 'success', { ms: fetchTime.toFixed(2) });
      if (forceRefresh) {
        toast({ title: 'Data Refreshed', description: 'Synced technician analytics.', status: 'success', duration: 2000, isClosable: true });
      }
    } catch (error) {
      addDebugLog(`Fetch failed: ${error.message}`, 'error', error);
      toast({ title: 'Error fetching technician data', description: error.message, status: 'error', duration: 5000, isClosable: true });
    } finally {
      setInternalLoading(false);
    }
  }, [externalData, addDebugLog, toast]);

  useEffect(() => { fetchTechnicianData(); }, [fetchTechnicianData]);

  // --- UPDATE: live refresh when work orders close or move between technicians (debounced 4 s)
  const liveTimer = useRef(null);
  useEffect(() => {
    if (externalData) return undefined;
    const LIVE = new Set(['completed', 'status_updated', 'assigned', 'reassigned', 'closed', 'updated']);
    const onWo = (msg) => {
      const body = liveEventBody(msg);
      if (body?.type && !LIVE.has(body.type)) return;
      clearTimeout(liveTimer.current);
      liveTimer.current = setTimeout(() => fetchTechnicianData(false), 4000);
    };
    const off = webSocketService?.subscribe?.('work_order_events', onWo);
    return () => {
      clearTimeout(liveTimer.current);
      if (typeof off === 'function') off(); else webSocketService?.unsubscribe?.('work_order_events', onWo);
    };
  }, [externalData, fetchTechnicianData]);
  // --- END UPDATE

  // ── Sorting Handler ────────────────────────────────────────────────────────
  const handleSort = useCallback((field) => {
    setSortConfig(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'desc' ? 'asc' : 'desc',
    }));
    setCurrentPage(1);
    addDebugLog(`Sort applied: ${field}`, 'info');
  }, [addDebugLog]);

  // ── Filtered + Sorted + Paginated Data ────────────────────────────────────
  const { filteredData, totalCount, pageCount, summary } = useMemo(() => {
    const startTime = performance.now();

    if (!activeData || activeData.length === 0) {
      addDebugLog('ProcessedData aborted: activeData empty', 'warn');
      return { filteredData: [], totalCount: 0, pageCount: 0, summary: null };
    }

    // ── Filter ──────────────────────────────────────────────────────────────
    let result = activeData.filter(tech => {
      const matchSearch = (tech.technician_name || '').toLowerCase().includes(searchTerm.toLowerCase());
      if (!matchSearch) return false;
      const fixRate = parseFloat(tech.first_time_fix_rate);
      const resTime = parseFloat(tech.avg_resolution_time_hours);
      if (filterMetric === 'high-performers')   return fixRate > 0.8 && resTime < 24;
      if (filterMetric === 'needs-improvement') return fixRate < 0.6 || resTime > 48;
      if (filterMetric === 'top-10')            return true; // sliced after sort
      return true;
    });

    // ── Sort ────────────────────────────────────────────────────────────────
    result = [...result].sort((a, b) => {
      let aVal = a[sortConfig.field] ?? 0;
      let bVal = b[sortConfig.field] ?? 0;
      if (sortConfig.field === 'technician_name') {
        aVal = String(aVal).toLowerCase();
        bVal = String(bVal).toLowerCase();
        return sortConfig.direction === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      aVal = parseFloat(aVal) || 0;
      bVal = parseFloat(bVal) || 0;
      return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
    });

    if (filterMetric === 'top-10') result = result.slice(0, 10);

    const totalCount = result.length;

    // ── Summary Stats ───────────────────────────────────────────────────────
    const avgFtf     = totalCount > 0 ? result.reduce((s, t) => s + parseFloat(t.first_time_fix_rate || 0), 0) / totalCount : 0;
    const avgRes     = totalCount > 0 ? result.reduce((s, t) => s + parseFloat(t.avg_resolution_time_hours || 0), 0) / totalCount : 0;
    const totalWOs   = result.reduce((s, t) => s + (parseInt(t.completed_work_orders) || 0), 0);
    const excellent  = result.filter(t => parseFloat(t.first_time_fix_rate) > 0.8 && parseFloat(t.avg_resolution_time_hours) < 24).length;
    const needsWork  = result.filter(t => parseFloat(t.first_time_fix_rate) < 0.6 || parseFloat(t.avg_resolution_time_hours) > 48).length;

    let trend = null;
    if (historicalData && Array.isArray(historicalData)) {
      const histAvgFtf = historicalData.reduce((s, t) => s + parseFloat(t.first_time_fix_rate || 0), 0) / (historicalData.length || 1);
      if (histAvgFtf > 0) {
        const change = ((avgFtf - histAvgFtf) / histAvgFtf) * 100;
        trend = { direction: change > 0 ? 'increase' : 'decrease', percentage: Math.abs(change).toFixed(1) };
      }
    }

    const summary = { avgFtf, avgRes, totalWOs, excellent, needsWork, trend };

    // ── Paginate ────────────────────────────────────────────────────────────
    const pageCount   = Math.max(1, Math.ceil(totalCount / pageSize));
    const safePage    = Math.min(currentPage, pageCount);
    const paginated   = result.slice((safePage - 1) * pageSize, safePage * pageSize);

    const ms = performance.now() - startTime;
    setPerformanceMetrics({ renderTime: ms.toFixed(2), dataPoints: totalCount, lastUpdate: new Date() });
    addDebugLog('Table data processed', 'success', { total: totalCount, page: safePage, ms: ms.toFixed(2) });

    return { filteredData: paginated, totalCount, pageCount, summary };
  }, [activeData, searchTerm, filterMetric, sortConfig, currentPage, pageSize, historicalData, addDebugLog]);

  // --- UPDATE: FlowSense rule findings over ALL technicians (not just the visible page)
  const techExplainer = useMemo(() => {
    const rows = (Array.isArray(activeData) ? activeData : []).map(t => ({
      name: t.technician_name || 'Unnamed technician',
      completed: parseInt(t.completed_work_orders, 10) || 0,
      ftf: Number.isFinite(parseFloat(t.first_time_fix_rate)) ? parseFloat(t.first_time_fix_rate) : null,
      hours: Number.isFinite(parseFloat(t.avg_resolution_time_hours)) ? parseFloat(t.avg_resolution_time_hours) : null,
    }));
    const findings = [];
    if (!rows.length) return { findings, facts: {} };
    const coach = rows.filter(r => (r.ftf !== null && r.ftf < 0.6) || (r.hours !== null && r.hours > 48));
    const slow = rows.filter(r => r.hours !== null && r.hours > 48).sort((a, b) => b.hours - a.hours);
    const rated = rows.filter(r => r.ftf !== null && r.hours !== null);
    const best = [...rated].sort((a, b) => (b.ftf - a.ftf) || (a.hours - b.hours))[0];
    const total = rows.reduce((s, r) => s + r.completed, 0);
    const busiest = [...rows].sort((a, b) => b.completed - a.completed)[0];
    const sortedLoads = rows.map(r => r.completed).sort((a, b) => a - b);
    const median = sortedLoads[Math.floor(sortedLoads.length / 2)] || 0;
    const unrated = rows.length - rated.length;
    if (coach.length) findings.push({ severity: coach.length > rows.length / 3 ? 'high' : 'medium', text: `${coach.length} of ${rows.length} technicians are below a 60% first-time fix rate or above 48 h average resolution: ${coach.slice(0, 4).map(r => r.name).join(', ')}${coach.length > 4 ? '…' : ''}.` });
    if (slow.length) findings.push({ severity: 'medium', text: `Slowest average close: ${slow[0].name} at ${slow[0].hours.toFixed(1)} h.` });
    if (busiest && total > 0 && median > 0 && busiest.completed >= median * 2 && rows.length > 2) findings.push({ severity: 'medium', text: `Workload is uneven: ${busiest.name} closed ${busiest.completed} (${((busiest.completed / total) * 100).toFixed(0)}% of all), the median technician closed ${median}.` });
    if (best) findings.push({ severity: 'ok', text: `Strongest record: ${best.name} — ${(best.ftf * 100).toFixed(0)}% first-time fix, ${best.hours.toFixed(1)} h average.` });
    if (unrated) findings.push({ severity: 'info', text: `${unrated} technician${unrated > 1 ? 's have' : ' has'} no fix-rate or resolution-time data yet.` });
    return {
      findings,
      facts: {
        technicians: rows.length, completed_total: total, median_completed: median,
        thresholds: { excellent: 'FTF > 80% and < 24 h', good: 'FTF ≥ 60% and ≤ 48 h', needs_training: 'otherwise' },
        rows: [...rows].sort((a, b) => b.completed - a.completed).slice(0, 25),
      },
    };
  }, [activeData]);
  // --- END UPDATE

  // ── Export ─────────────────────────────────────────────────────────────────
  const handleExportCSV = useCallback(() => {
    if (!activeData || activeData.length === 0) return;
    try {
      const headers = ['Rank', 'Technician', 'Completed WOs', 'Avg Resolution (hrs)', 'FTF Rate (%)', 'Performance'];
      const rows = activeData.map((tech, i) => {
        const fixRate = parseFloat(tech.first_time_fix_rate);
        const resTime = parseFloat(tech.avg_resolution_time_hours);
        const tier    = getTier(fixRate, resTime);
        return [i + 1, `"${String(tech.technician_name || '').replace(/"/g, '""')}"`, tech.completed_work_orders ?? '', Number.isFinite(resTime) ? resTime.toFixed(1) : '', Number.isFinite(fixRate) ? (fixRate * 100).toFixed(1) : '', tier.label].join(','); // --- UPDATE: quoted names, blanks for missing
      });
      const csv      = [headers.join(','), ...rows].join('\n');
      const dataUri  = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv);
      const link     = document.createElement('a');
      link.setAttribute('href', dataUri);
      link.setAttribute('download', `technician-performance-${Date.now()}.csv`);
      link.click();
      addDebugLog('CSV exported successfully', 'success');
      toast({ title: 'Export Complete', description: 'Technician data exported as CSV.', status: 'success', duration: 2000, isClosable: true });
    } catch (error) {
      addDebugLog(`Export failed: ${error.message}`, 'error', error);
      toast({ title: 'Export Failed', description: error.message, status: 'error', duration: 3000, isClosable: true });
    }
  }, [activeData, addDebugLog, toast]);

  // ── Stats Row ──────────────────────────────────────────────────────────────
  const renderStats = () => {
    if (!summary) return null;
    const statItems = [
      {
        label:  'Total Work Orders',
        value:  summary.totalWOs.toLocaleString(),
        help:   `${totalCount} technicians`,
        scheme: 'blue',
      },
      {
        label:  'Avg FTF Rate',
        value:  `${(summary.avgFtf * 100).toFixed(1)}%`,
        help:   summary.trend ? null : 'First-time fix',
        scheme: 'green',
        trend:  summary.trend,
      },
      {
        label:  'Avg Resolution',
        value:  `${summary.avgRes.toFixed(1)}h`,
        help:   'Mean close time',
        scheme: 'purple',
      },
      {
        label:  'Needs Training',
        value:  summary.needsWork,
        help:   `${summary.excellent} excellent`,
        scheme: summary.needsWork > 0 ? 'red' : 'green',
      },
    ];

    return (
      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={2} mb={4}>
        {statItems.map((item) => (
          <Stat
            key={item.label}
            size="sm"
            p={2}
            borderRadius="md"
            borderWidth="1px"
            borderColor={borderColor}
            bg={cardBg}
            transition="all 0.2s"
            _hover={{ borderColor: `${item.scheme}.400`, transform: 'translateY(-2px)' }}
          >
            <StatLabel fontSize="xs" color={mutedColor}>{item.label}</StatLabel>
            <StatNumber fontSize="lg">
              <Badge colorScheme={item.scheme} variant="subtle" fontSize="md" px={1}>
                {item.value}
              </Badge>
            </StatNumber>
            <StatHelpText fontSize="xs" mb={0}>
              {item.trend ? (
                <>
                  <StatArrow type={item.trend.direction === 'increase' ? 'increase' : 'decrease'} />
                  {item.trend.percentage}% vs prev
                </>
              ) : item.help}
            </StatHelpText>
          </Stat>
        ))}
      </SimpleGrid>
    );
  };

  // ── Pagination Controls ────────────────────────────────────────────────────
  const renderPagination = () => {
    const safePage = Math.min(currentPage, pageCount);
    const start    = (safePage - 1) * pageSize + 1;
    const end      = Math.min(safePage * pageSize, totalCount);

    return (
      <Flex justify="space-between" align="center" mt={3} flexWrap="wrap" gap={2}>
        <HStack spacing={1}>
          <Text fontSize="xs" color={mutedColor}>
            Showing <strong>{start}–{end}</strong> of <strong>{totalCount}</strong> technicians
          </Text>
        </HStack>
        <HStack spacing={2}>
          <Select
            size="xs"
            w="80px"
            value={pageSize}
            onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
            bg={inputBg}
          >
            {PAGE_SIZE_OPTIONS.map(n => <option key={n} value={n}>{n} / page</option>)}
          </Select>
          <IconButton
            icon={<MdChevronLeft />}
            size="xs"
            variant="outline"
            isDisabled={safePage <= 1}
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
          />
          {Array.from({ length: Math.min(5, pageCount) }, (_, i) => {
            const page = Math.max(1, Math.min(pageCount - 4, safePage - 2)) + i;
            if (page > pageCount) return null;
            return (
              <Button
                key={page}
                size="xs"
                variant={page === safePage ? 'solid' : 'outline'}
                colorScheme={page === safePage ? 'blue' : 'gray'}
                onClick={() => setCurrentPage(page)}
              >
                {page}
              </Button>
            );
          })}
          <IconButton
            icon={<MdChevronRight />}
            size="xs"
            variant="outline"
            isDisabled={safePage >= pageCount}
            onClick={() => setCurrentPage(p => Math.min(pageCount, p + 1))}
          />
        </HStack>
      </Flex>
    );
  };

  // ── Debug Console ──────────────────────────────────────────────────────────
  const renderDebugConsole = () => (
    <Collapse in={showDebug} animateOpacity>
      <Box mt={4} p={3} bg={debugBg} borderRadius="md" borderWidth="1px" borderColor={borderColor} maxH="300px" overflowY="auto">
        <HStack justify="space-between" mb={2}>
          <Text fontWeight="bold" fontSize="sm">Advanced Debug Console</Text>
          <HStack>
            <Badge colorScheme="blue">Renders: {renderCount.current}</Badge>
            <Button size="xs" onClick={() => setDebugLogs([])}>Clear</Button>
          </HStack>
        </HStack>
        <Box mb={2} p={2} bg={cardBg} borderRadius="md">
          <Text fontSize="xs" fontWeight="bold" mb={1}>Performance Metrics:</Text>
          <SimpleGrid columns={3} spacing={2} fontSize="xs">
            <Box><Text color="gray.500">Render Time:</Text><Text fontWeight="semibold">{performanceMetrics.renderTime}ms</Text></Box>
            <Box><Text color="gray.500">Data Points:</Text><Text fontWeight="semibold">{performanceMetrics.dataPoints}</Text></Box>
            <Box><Text color="gray.500">Last Update:</Text><Text fontWeight="semibold">{performanceMetrics.lastUpdate?.toLocaleTimeString() || 'N/A'}</Text></Box>
          </SimpleGrid>
        </Box>
        <Divider mb={2} />
        <VStack align="stretch" spacing={2} fontSize="xs">
          {debugLogs.map((log, index) => (
            <Box
              key={index} p={2} borderRadius="md"
              bg={log.type==='error'?'red.50':log.type==='warn'?'yellow.50':log.type==='success'?'green.50':'white'}
              borderWidth="1px"
              borderColor={log.type==='error'?'red.200':log.type==='warn'?'yellow.200':log.type==='success'?'green.200':'gray.200'}
            >
              <Text fontWeight="bold" color={log.type==='error'?'red.700':log.type==='warn'?'yellow.700':log.type==='success'?'green.700':'gray.700'}>
                [{new Date(log.timestamp).toLocaleTimeString()}] {log.type.toUpperCase()}: {log.message}
              </Text>
              {log.details && (
                <Code mt={1} p={1} w="100%" fontSize="10px" colorScheme="blackAlpha" whiteSpace="pre-wrap" display="block">
                  {JSON.stringify(log.details, null, 2)}
                </Code>
              )}
            </Box>
          ))}
          {debugLogs.length === 0 && <Text color="gray.500" textAlign="center">No logs yet</Text>}
        </VStack>
      </Box>
    </Collapse>
  );

  // ── Main Content ───────────────────────────────────────────────────────────
  const renderContent = () => {
    if (activeLoading) {
      return (
        <Flex justify="center" align="center" h="300px" direction="column">
          <Spinner size="xl" mb={4} color="blue.400" />
          <Text color={mutedColor}>Loading technician performance data...</Text>
          <Progress size="xs" isIndeterminate w="200px" mt={2} colorScheme="blue" />
        </Flex>
      );
    }

    if (!activeData || activeData.length === 0) {
      return (
        <Flex justify="center" align="center" h="200px" direction="column">
          <Icon as={MdPerson} boxSize={10} color="gray.300" mb={2} />
          <Text color={mutedColor} fontSize="lg">No technician data available.</Text>
        </Flex>
      );
    }

    if (filteredData.length === 0) {
      return (
        <Flex justify="center" align="center" h="150px" direction="column">
          <Icon as={MdSearch} boxSize={8} color="gray.300" mb={2} />
          <Text color={mutedColor}>No results match your filter criteria.</Text>
          <Button size="sm" mt={2} variant="ghost" onClick={() => { setSearchTerm(''); setFilterMetric('all'); }}>
            Clear Filters
          </Button>
        </Flex>
      );
    }

    return (
      <>
        <TableContainer
          borderRadius="md"
          borderWidth="1px"
          borderColor={borderColor}
          overflowX="auto"
        >
          <Table variant="simple" size="sm">
            <Thead bg={tableHeadBg}>
              <Tr>
                {visibleColumns.rank           && <Th w="40px">#</Th>}
                {visibleColumns.name           && (
                  <SortableTh
                    label="Technician"
                    field="technician_name"
                    sortConfig={sortConfig}
                    onSort={handleSort}
                    tooltip="Sort alphabetically"
                  />
                )}
                {visibleColumns.completedWOs   && (
                  <SortableTh
                    label="Completed WOs"
                    field="completed_work_orders"
                    sortConfig={sortConfig}
                    onSort={handleSort}
                    isNumeric
                    tooltip="Total closed work orders"
                  />
                )}
                {visibleColumns.resolutionTime && (
                  <SortableTh
                    label="Avg Resolution"
                    field="avg_resolution_time_hours"
                    sortConfig={sortConfig}
                    onSort={handleSort}
                    isNumeric
                    tooltip="Mean hours to close a work order"
                  />
                )}
                {visibleColumns.ftfRate        && (
                  <SortableTh
                    label="FTF Rate"
                    field="first_time_fix_rate"
                    sortConfig={sortConfig}
                    onSort={handleSort}
                    tooltip="First-time fix rate — higher is better"
                  />
                )}
                {visibleColumns.performance    && <Th>Performance</Th>}
              </Tr>
            </Thead>
            <Tbody>
              {filteredData.map((tech, i) => {
                const globalRank = (Math.min(currentPage, pageCount) - 1) * pageSize + i + 1;
                return (
                  <TechnicianRow
                    key={tech.technician_name || i}
                    tech={tech}
                    rank={globalRank}
                    textColor={textColor}
                    tableRowHoverBg={tableRowHoverBg}
                    visibleColumns={visibleColumns}
                    isSelected={selectedRow === (tech.technician_name || i)}
                    onSelect={() => setSelectedRow(prev =>
                      prev === (tech.technician_name || i) ? null : (tech.technician_name || i)
                    )}
                  />
                );
              })}
            </Tbody>
          </Table>
        </TableContainer>
        {renderPagination()}
      </>
    );
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Card
      p={5}
      h={isFullscreen ? '100vh' : '100%'}
      position={isFullscreen ? 'fixed' : 'relative'}
      inset={isFullscreen ? 0 : undefined}
      zIndex={isFullscreen ? 'modal' : undefined}
      overflowY={isFullscreen ? 'auto' : undefined}
      borderRadius={isFullscreen ? 0 : undefined}
    >
      <VStack align="stretch" spacing={4}>
        {/* ── Header ───────────────────────────────────────────────────────── */}
        <Flex justify="space-between" align="center" flexWrap="wrap" gap={3}>
          <HStack>
            <Heading size="md">Technician Performance</Heading>
            {summary && (
              <Badge colorScheme="blue" fontSize="sm">{totalCount} Techs</Badge>
            )}
          </HStack>

          <HStack spacing={2} flexWrap="wrap">
            {/* Search */}
            <InputGroup size="sm" w={{ base: 'full', md: '180px' }}>
              <InputLeftElement pointerEvents="none">
                <Icon as={MdSearch} color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder="Search technician..."
                value={searchTerm}
                onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                bg={inputBg}
                borderRadius="md"
              />
            </InputGroup>

            {/* Filter */}
            <Select
              size="sm"
              w={{ base: 'full', md: '160px' }}
              value={filterMetric}
              onChange={e => { setFilterMetric(e.target.value); setCurrentPage(1); }}
              bg={inputBg}
            >
              <option value="all">All Technicians</option>
              <option value="high-performers">High Performers</option>
              <option value="needs-improvement">Needs Improvement</option>
              <option value="top-10">Top 10</option>
            </Select>

            {/* Refresh */}
            {!externalData && (
              <Tooltip label="Refresh Data">
                <IconButton
                  icon={<MdRefresh />}
                  size="sm"
                  variant="outline"
                  isLoading={activeLoading}
                  onClick={() => fetchTechnicianData(true)}
                />
              </Tooltip>
            )}

            {/* Export CSV */}
            {enableExport && (
              <Tooltip label="Export as CSV">
                <IconButton
                  icon={<MdDownload />}
                  size="sm"
                  variant="outline"
                  onClick={handleExportCSV}
                  isDisabled={!activeData || activeData.length === 0 || activeLoading}
                />
              </Tooltip>
            )}

            {/* Settings */}
            <Tooltip label="Table settings">
              <IconButton
                icon={<MdSettings />}
                size="sm"
                variant="outline"
                onClick={onSettingsOpen}
              />
            </Tooltip>

            {/* Debug */}
            {enableDebug && (
              <Tooltip label="Toggle debug console">
                <IconButton
                  icon={<MdBugReport />}
                  size="sm"
                  variant="outline"
                  colorScheme={showDebug ? 'red' : 'gray'}
                  onClick={() => setShowDebug(!showDebug)}
                />
              </Tooltip>
            )}

            {/* Fullscreen */}
            <Tooltip label={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}>
              <IconButton
                icon={isFullscreen ? <MdFullscreenExit /> : <MdFullscreen />}
                size="sm"
                variant="outline"
                onClick={() => setIsFullscreen(!isFullscreen)}
              />
            </Tooltip>
          </HStack>
        </Flex>

        {/* ── Stats Row ─────────────────────────────────────────────────────── */}
        {renderStats()}

        {/* --- UPDATE: FlowSense AI explainer */}
        {!activeLoading && Array.isArray(activeData) && activeData.length > 0 && (
          <FlowSenseAIExplainer
            module="technician_performance"
            title="Explain technician performance"
            subtitle="First-time fix, resolution time and workload"
            facts={techExplainer.facts}
            findings={techExplainer.findings}
            compact
            questions={[
              'Who needs coaching and on what?',
              'Is work spread fairly across the team?',
              'What would raise the first-time fix rate?',
            ]}
          />
        )}
        {/* --- END UPDATE */}

        {/* ── Table Content ─────────────────────────────────────────────────── */}
        {renderContent()}

        {/* ── Debug Console ─────────────────────────────────────────────────── */}
        {enableDebug && renderDebugConsole()}
      </VStack>

      {/* ── Settings Modal ──────────────────────────────────────────────────── */}
      <Modal isOpen={isSettingsOpen} onClose={onSettingsClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Table Settings</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack spacing={4} align="stretch">
              <Text fontWeight="semibold" fontSize="sm" color={mutedColor} textTransform="uppercase" letterSpacing="wider">
                Visible Columns
              </Text>
              {[
                { key: 'rank',           label: 'Rank (#)'                    },
                { key: 'name',           label: 'Technician Name'             },
                { key: 'completedWOs',   label: 'Completed Work Orders'       },
                { key: 'resolutionTime', label: 'Avg. Resolution Time'        },
                { key: 'ftfRate',        label: 'First-Time Fix Rate'         },
                { key: 'performance',    label: 'Performance Tier Badge'      },
              ].map(({ key, label }) => (
                <FormControl key={key} display="flex" alignItems="center" justifyContent="space-between">
                  <FormLabel fontSize="sm" mb={0}>{label}</FormLabel>
                  <Switch
                    isChecked={visibleColumns[key]}
                    onChange={(e) => setVisibleColumns(prev => ({ ...prev, [key]: e.target.checked }))}
                  />
                </FormControl>
              ))}

              <Divider />

              <FormControl>
                <FormLabel fontSize="sm">Rows Per Page</FormLabel>
                <Select
                  size="sm"
                  value={pageSize}
                  onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
                >
                  {PAGE_SIZE_OPTIONS.map(n => <option key={n} value={n}>{n} rows</option>)}
                </Select>
              </FormControl>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Card>
  );
};

export default TechnicianPerformanceWidget;